"use client";

import React, { useEffect, useState } from "react";
import { supabase } from "../supabase/client";
import { 
  Ticket, Plus,
  Hash, Percent, ToggleLeft, ToggleRight, 
  Loader2, X, Save, History, User, Edit3
} from "lucide-react";

export default function CouponManager() {
  const [coupons, setCoupons] = useState([]);
  const [usages, setUsages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(null); 
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null); // Track if editing
  
  const [form, setForm] = useState({
    code: "",
    discount_type: "percent",
    value: "",
    min_order_amount: 0,
    expiry_date: "",
    max_usages: 10,
    is_active: true
  });

  useEffect(() => { fetchCoupons(); }, []);

  async function fetchCoupons() {
    setLoading(true);
    const { data, error } = await supabase
      .from("coupons")
      .select("*")
      .order("id", { ascending: false });
    
    if (!error) setCoupons(data || []);
    setLoading(false);
  }

  async function fetchUsageHistory(couponId) {
    const { data, error } = await supabase
      .from("coupon_usages")
      .select(`
        *,
        app_users:user_id (full_name, phone),
        orders:order_id (id, total_amount)
      `)
      .eq("coupon_id", couponId)
      .order("used_at", { ascending: false });

    if (!error) setUsages(data || []);
    setShowHistory(couponId);
  }

  // --- ADDED EDIT LOGIC ---
  const handleEdit = (coupon) => {
    setEditingId(coupon.id);
    setForm({
      code: coupon.code,
      discount_type: coupon.discount_type,
      value: coupon.value,
      min_order_amount: coupon.min_order_amount,
      expiry_date: coupon.expiry_date ? coupon.expiry_date.slice(0, 16) : "", // Format for datetime-local
      max_usages: coupon.max_usages,
      is_active: coupon.is_active
    });
    setIsModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    let error;
    if (editingId) {
      // Update logic
      const { error: updateError } = await supabase
        .from("coupons")
        .update(form)
        .eq("id", editingId);
      error = updateError;
    } else {
      // Insert logic
      const { error: insertError } = await supabase
        .from("coupons")
        .insert([form]);
      error = insertError;
    }

    if (error) {
      alert("Error: " + error.message);
    } else {
      setIsModalOpen(false);
      resetForm();
      fetchCoupons();
    }
    setSaving(false);
  };

  const resetForm = () => {
    setForm({ code: "", discount_type: "percent", value: "", min_order_amount: 0, expiry_date: "", max_usages: 10, is_active: true });
    setEditingId(null);
  };

  const toggleStatus = async (id, currentStatus) => {
    const { error } = await supabase.from("coupons").update({ is_active: !currentStatus }).eq("id", id);
    if (!error) setCoupons(prev => prev.map(c => c.id === id ? { ...c, is_active: !currentStatus } : c));
  };

  const deleteCoupon = async (id) => {
    if (!window.confirm("Are you sure? This will remove the coupon and all its usage records.")) return;
    const { error } = await supabase.from("coupons").delete().eq("id", id);
    if (!error) setCoupons(prev => prev.filter(c => c.id !== id));
  };

  return (
    <div className="min-h-screen bg-slate-50/50 p-4 md:p-10 font-sans text-slate-900">
      <div className="max-w-7xl mx-auto">
        
        {/* --- HEADER --- */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-10 gap-6">
          <div>
            <h1 className="text-3xl font-black tracking-tighter uppercase italic flex items-center gap-3">
              <Ticket className="text-indigo-600" size={32} /> PROMO_SYSTEM
            </h1>
            <p className="text-slate-500 text-sm font-medium">Create and track high-conversion discount campaigns.</p>
          </div>

          <button 
            onClick={() => { resetForm(); setIsModalOpen(true); }}
            className="bg-slate-900 text-white px-8 py-4 rounded-[2rem] text-[11px] font-black uppercase tracking-widest flex items-center gap-2 hover:bg-indigo-600 transition-all shadow-xl shadow-slate-200"
          >
            <Plus size={18} strokeWidth={3} /> Create New Campaign
          </button>
        </div>

        {/* --- COUPON LIST --- */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {loading ? (
            <div className="col-span-full py-20 text-center animate-pulse text-slate-300 font-bold uppercase tracking-[0.4em] text-xs">Syncing active promos...</div>
          ) : coupons.map((c) => {
            const usagePercent = (c.current_usage_count / c.max_usages) * 100;
            return (
              <div key={c.id} className={`bg-white rounded-[3rem] p-8 border border-slate-100 shadow-sm transition-all relative overflow-hidden group ${!c.is_active ? 'opacity-50 grayscale' : 'hover:border-indigo-200 hover:shadow-2xl hover:shadow-indigo-50'}`}>
                
                {/* Status & History Actions */}
                <div className="absolute top-8 right-8 flex items-center gap-2">
                  <button onClick={() => handleEdit(c)} className="p-2 bg-slate-50 rounded-xl text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-all">
                    <Edit3 size={18} />
                  </button>
                  <button onClick={() => fetchUsageHistory(c.id)} className="p-2 bg-slate-50 rounded-xl text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-all">
                    <History size={18} />
                  </button>
                  <button onClick={() => toggleStatus(c.id, c.is_active)}>
                    {c.is_active ? <ToggleRight size={36} className="text-indigo-600" /> : <ToggleLeft size={36} className="text-slate-300" />}
                  </button>
                </div>

                <div className="mb-8">
                  <span className="text-[9px] font-black text-indigo-500 uppercase tracking-widest bg-indigo-50 px-3 py-1 rounded-full border border-indigo-100">
                    {c.discount_type === 'percent' ? 'Variable Percentage' : 'Flat Discount'}
                  </span>
                  <h3 className="text-4xl font-black italic mt-4 tracking-tighter text-slate-900 uppercase leading-none">
                    {c.code}
                  </h3>
                </div>

                {/* Usage Progress Bar */}
                <div className="mb-6">
                  <div className="flex justify-between items-end mb-2">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Usage Limit</p>
                    <p className="text-[11px] font-black text-slate-900">{c.current_usage_count} / {c.max_usages}</p>
                  </div>
                  <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-indigo-600 transition-all duration-700" style={{ width: `${Math.min(usagePercent, 100)}%` }} />
                  </div>
                </div>

                <div className="space-y-3 pt-4 border-t border-slate-50">
                  <div className="flex justify-between text-sm"><span className="text-slate-400 font-medium">Value</span><span className="font-black italic text-indigo-600">{c.discount_type === 'percent' ? `${c.value}%` : `৳${c.value}`} OFF</span></div>
                  <div className="flex justify-between text-sm"><span className="text-slate-400 font-medium">Min Order</span><span className="font-bold text-slate-900">৳{c.min_order_amount}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-slate-400 font-medium">Expiry</span><span className="font-bold text-slate-900 text-xs uppercase">{c.expiry_date ? new Date(c.expiry_date).toLocaleDateString() : 'Infinite'}</span></div>
                </div>

                <button onClick={() => deleteCoupon(c.id)} className="mt-8 w-full py-3 bg-slate-50 rounded-2xl text-[10px] font-black uppercase text-slate-400 hover:bg-red-50 hover:text-red-600 transition-all opacity-0 group-hover:opacity-100">
                    Terminate Coupon
                </button>
              </div>
            );
          })}
        </div>

        {/* --- USAGE HISTORY SLIDE-OVER --- */}
        {showHistory && (
          <div className="fixed inset-0 z-[110] flex justify-end bg-slate-900/40 backdrop-blur-sm">
            <div className="w-full max-w-lg bg-white h-full shadow-2xl p-10 animate-in slide-in-from-right duration-300 overflow-y-auto">
              <div className="flex justify-between items-center mb-10">
                <div>
                  <h2 className="text-2xl font-black italic uppercase">Usage_Log</h2>
                  <p className="text-xs text-slate-400 font-medium uppercase tracking-widest mt-1">Real-time engagement tracking</p>
                </div>
                <button onClick={() => setShowHistory(null)} className="p-3 bg-slate-50 rounded-2xl hover:text-red-500 transition-all"><X size={20}/></button>
              </div>

              {usages.length === 0 ? (
                <div className="py-20 text-center border-2 border-dashed border-slate-100 rounded-[2rem]">
                  <p className="text-xs font-bold text-slate-300 uppercase tracking-widest">No redemptions yet</p>
                </div>
              ) : usages.map((u) => (
                <div key={u.id} className="mb-4 p-5 bg-slate-50 rounded-3xl flex justify-between items-center group">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-indigo-600 shadow-sm"><User size={18}/></div>
                    <div>
                      <p className="text-sm font-black uppercase italic">{u.app_users?.full_name}</p>
                      <p className="text-[10px] text-slate-400 font-bold">{new Date(u.used_at).toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-black text-slate-900">৳{u.orders?.total_amount}</div>
                    <div className="text-[9px] text-indigo-500 font-bold tracking-tighter uppercase">Order #{u.orders?.id.slice(0,6)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* --- CREATE/EDIT CAMPAIGN MODAL --- */}
        {isModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-md p-4">
            <div className="bg-white w-full max-w-md rounded-[3rem] shadow-2xl p-10">
              <h2 className="text-2xl font-black italic uppercase tracking-tighter mb-8">
                {editingId ? "Update_Campaign" : "Deploy_Campaign"}
              </h2>
              
              <form onSubmit={handleSave} className="space-y-5">
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2 block px-2">Unique Code</label>
                  <div className="relative">
                    <Hash className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-300" size={18}/>
                    <input 
                      required placeholder="E.G. RAMADAN25"
                      className="w-full pl-14 pr-6 py-4 bg-slate-50 border-none rounded-2xl outline-none focus:ring-2 focus:ring-indigo-600 font-black uppercase text-sm"
                      value={form.code} onChange={e => setForm({...form, code: e.target.value.toUpperCase()})}
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2 block px-2">
                    Expiry Date
                  </label>
                  <input 
                    type="datetime-local"
                    className="w-full p-4 bg-slate-50 border-none rounded-2xl outline-none focus:ring-2 focus:ring-indigo-600 font-black text-sm"
                    value={form.expiry_date} 
                    onChange={e => setForm({...form, expiry_date: e.target.value})}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2 block px-2">Reward Type</label>
                    <select 
                      className="w-full p-4 bg-slate-50 border-none rounded-2xl outline-none focus:ring-2 focus:ring-indigo-600 font-bold text-xs"
                      value={form.discount_type} onChange={e => setForm({...form, discount_type: e.target.value})}
                    >
                      <option value="percent">Percent %</option>
                      <option value="fixed">Fixed BDT</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2 block px-2">Amount</label>
                    <div className="relative">
                      <Percent className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-300" size={18}/>
                      <input 
                        required type="number" className="w-full pl-14 pr-6 py-4 bg-slate-50 border-none rounded-2xl outline-none focus:ring-2 focus:ring-indigo-600 font-black text-sm"
                        value={form.value} onChange={e => setForm({...form, value: e.target.value})}
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2 block px-2">Max Capacity</label>
                    <input 
                      type="number" className="w-full p-4 bg-slate-50 border-none rounded-2xl outline-none focus:ring-2 focus:ring-indigo-600 font-black text-sm"
                      value={form.max_usages} onChange={e => setForm({...form, max_usages: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2 block px-2">Min Order</label>
                    <input 
                      type="number" className="w-full p-4 bg-slate-50 border-none rounded-2xl outline-none focus:ring-2 focus:ring-indigo-600 font-black text-sm"
                      value={form.min_order_amount} onChange={e => setForm({...form, min_order_amount: e.target.value})}
                    />
                  </div>
                </div>

                <button 
                  disabled={saving}
                  className="w-full py-5 bg-slate-900 text-white rounded-[2.5rem] font-black uppercase tracking-[0.3em] italic mt-6 hover:bg-indigo-600 transition-all flex items-center justify-center gap-3 disabled:opacity-50 shadow-2xl shadow-indigo-100"
                >
                  {saving ? <Loader2 className="animate-spin" size={20}/> : <Save size={20}/>}
                  {editingId ? "Update Changes" : "Activate Promo"}
                </button>
                <button type="button" onClick={() => setIsModalOpen(false)} className="w-full text-[10px] font-black uppercase tracking-widest text-slate-300 hover:text-slate-600">Cancel</button>
              </form>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}